import React, { useState, useEffect, useCallback } from "react";
import { authFetch, getUser } from "../pages/Auth";
import { COLORS } from "../chartTheme";
import IngestionPanel from "./IngestionPanel";
import LinkedInAgentPanel from "./LinkedInAgentPanel";

// ═══════════════════════════════════════════════════════════
// SHARED
// ═══════════════════════════════════════════════════════════

const cardStyle = {
  background: COLORS.bg, border: `1px solid ${COLORS.border}`,
  borderRadius: 10, padding: 20, marginBottom: 16,
};

function Spinner() {
  return <div style={{ textAlign: "center", padding: 30, color: COLORS.muted, fontSize: 12 }}>Loading...</div>;
}

function ErrorBanner({ msg, onRetry }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: "rgba(255,77,109,0.08)", border: "1px solid rgba(255,77,109,0.25)",
      borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#ff4d6d",
    }}>
      {msg}
      {onRetry && <button onClick={onRetry} style={s.retryBtn}>Retry</button>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// USER MANAGEMENT
// ═══════════════════════════════════════════════════════════

function UserManagement() {
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [form, setForm] = useState({ email: "", password: "", role: "viewer", customer_id: "" });

  const user = getUser();
  const isSuperAdmin = user?.role === "super_admin";

  const loadUsers = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await authFetch("/api/admin/users");
      const data = await res.json();
      if (res.ok) setUsers(data.users);
      else setError(data.error || "Failed to load users");
    } catch { setError("Unable to connect"); }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true); setCreateError("");
    try {
      const body = { email: form.email, password: form.password, role: form.role };
      if (isSuperAdmin && form.customer_id) body.customer_id = form.customer_id;
      const res = await authFetch("/api/admin/users", {
        method: "POST", body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setShowCreate(false);
        setForm({ email: "", password: "", role: "viewer", customer_id: "" });
        loadUsers();
      } else { setCreateError(data.error || "Failed to create user"); }
    } catch { setCreateError("Unable to connect"); }
    setCreating(false);
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={s.secTitle}>Users</div>
        <button onClick={() => setShowCreate(!showCreate)} style={s.addBtn}>
          {showCreate ? "Cancel" : "+ New User"}
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} style={s.form}>
          {createError && <ErrorBanner msg={createError} />}
          <div style={s.formGrid}>
            <input type="email" placeholder="Email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} style={s.input} />
            <input type="password" placeholder="Password (min 12 chars)" required minLength={12}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} style={s.input} />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={s.input}>
              <option value="viewer">Viewer</option>
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
            {isSuperAdmin && (
              <input type="text" placeholder="Customer ID" required value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })} style={s.input} />
            )}
          </div>
          <button type="submit" disabled={creating} style={s.submitBtn}>
            {creating ? "Creating..." : "Create User"}
          </button>
        </form>
      )}

      {loading ? <Spinner /> : error ? <ErrorBanner msg={error} onRetry={loadUsers} /> : (
        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Email</th><th style={s.th}>Role</th>
              <th style={s.th}>Customer</th><th style={s.th}>Status</th><th style={s.th}>Created</th>
            </tr></thead>
            <tbody>
              {(users || []).map((u) => (
                <tr key={u.user_id}>
                  <td style={{ ...s.td, color: COLORS.text, fontWeight: 600 }}>{u.email}</td>
                  <td style={s.td}>
                    <span style={{
                      ...s.badge,
                      background: u.role === "admin" ? "rgba(124,58,237,0.15)" : u.role === "operator" ? "rgba(0,229,255,0.15)" : "rgba(75,85,99,0.15)",
                      color: u.role === "admin" ? "#a78bfa" : u.role === "operator" ? "#00e5ff" : "#9ca3af",
                    }}>{u.role}</span>
                  </td>
                  <td style={{ ...s.td, fontFamily: "monospace", fontSize: 10 }}>{u.customer_id?.slice(0, 12)}</td>
                  <td style={s.td}>
                    <span style={{
                      ...s.badge,
                      background: u.is_active ? "rgba(52,211,153,0.15)" : "rgba(255,77,109,0.15)",
                      color: u.is_active ? "#34d399" : "#ff4d6d",
                    }}>{u.is_active ? "Active" : "Inactive"}</span>
                  </td>
                  <td style={{ ...s.td, fontSize: 10, color: COLORS.muted }}>{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!users || users.length === 0) && (
            <div style={{ textAlign: "center", padding: 20, color: COLORS.muted, fontSize: 12 }}>No users found</div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CUSTOMER MANAGEMENT (super_admin only)
// ═══════════════════════════════════════════════════════════

function CustomerManagement() {
  const [customers, setCustomers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [name, setName] = useState("");

  const loadCustomers = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await authFetch("/api/admin/customers");
      const data = await res.json();
      if (res.ok) setCustomers(data.customers);
      else setError(data.error || "Failed to load");
    } catch { setError("Unable to connect"); }
    setLoading(false);
  }, []);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true); setCreateError("");
    try {
      const res = await authFetch("/api/admin/customers", {
        method: "POST", body: JSON.stringify({ customer_name: name }),
      });
      const data = await res.json();
      if (res.ok) {
        setShowCreate(false); setName(""); loadCustomers();
      } else { setCreateError(data.error || "Failed"); }
    } catch { setCreateError("Unable to connect"); }
    setCreating(false);
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={s.secTitle}>Customers</div>
        <button onClick={() => setShowCreate(!showCreate)} style={s.addBtn}>
          {showCreate ? "Cancel" : "+ New Customer"}
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} style={s.form}>
          {createError && <ErrorBanner msg={createError} />}
          <div style={{ display: "flex", gap: 8 }}>
            <input type="text" placeholder="Customer name" required value={name}
              onChange={(e) => setName(e.target.value)} style={{ ...s.input, flex: 1 }} />
            <button type="submit" disabled={creating} style={s.submitBtn}>
              {creating ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      )}

      {loading ? <Spinner /> : error ? <ErrorBanner msg={error} onRetry={loadCustomers} /> : (
        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Name</th><th style={s.th}>Customer ID</th>
              <th style={s.th}>Patients</th><th style={s.th}>Status</th><th style={s.th}>Contract</th>
            </tr></thead>
            <tbody>
              {(customers || []).map((c) => (
                <tr key={c.customer_id}>
                  <td style={{ ...s.td, color: COLORS.text, fontWeight: 600 }}>{c.customer_name}</td>
                  <td style={{ ...s.td, fontFamily: "monospace", fontSize: 10 }}>{c.customer_id}</td>
                  <td style={s.td}>{c.patient_count ?? 0}</td>
                  <td style={s.td}>
                    <span style={{
                      ...s.badge,
                      background: c.is_active ? "rgba(52,211,153,0.15)" : "rgba(255,77,109,0.15)",
                      color: c.is_active ? "#34d399" : "#ff4d6d",
                    }}>{c.is_active ? "Active" : "Inactive"}</span>
                  </td>
                  <td style={{ ...s.td, fontSize: 10 }}>
                    {c.contract_start ? `${fmtDate(c.contract_start)} — ${fmtDate(c.contract_end)}` : "\u2014"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!customers || customers.length === 0) && (
            <div style={{ textAlign: "center", padding: 20, color: COLORS.muted, fontSize: 12 }}>No customers found</div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ENHANCED AUDIT LOG
// ═══════════════════════════════════════════════════════════

function EnhancedAuditLog() {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const loadAudit = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await authFetch("/api/admin/audit-log?limit=100");
      const data = await res.json();
      if (res.ok) setEvents(data.events);
      else setError(data.error || "Failed");
    } catch { setError("Unable to connect"); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const eventTypes = events ? [...new Set(events.map((e) => e.event_type).filter(Boolean))] : [];
  const filtered = events && typeFilter
    ? events.filter((e) => e.event_type === typeFilter)
    : events;

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={s.secTitle}>Audit Log</div>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => setTypeFilter("")}
            style={{ ...s.filterBtn, background: !typeFilter ? "rgba(0,229,255,0.1)" : "#131920", color: !typeFilter ? COLORS.primary : COLORS.muted }}>
            All
          </button>
          {eventTypes.map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)}
              style={{ ...s.filterBtn, background: typeFilter === t ? "rgba(0,229,255,0.1)" : "#131920", color: typeFilter === t ? COLORS.primary : COLORS.muted }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : error ? <ErrorBanner msg={error} onRetry={loadAudit} /> : (
        <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Timestamp</th><th style={s.th}>Event</th>
              <th style={s.th}>User</th><th style={s.th}>IP Address</th>
            </tr></thead>
            <tbody>
              {(filtered || []).map((e) => (
                <tr key={e.event_id}>
                  <td style={{ ...s.td, fontSize: 10, fontFamily: "monospace" }}>{fmtDate(e.timestamp)}</td>
                  <td style={s.td}>
                    <span style={{
                      ...s.badge,
                      background: e.event_type === "login" ? "rgba(52,211,153,0.15)" : e.event_type === "failed_login" ? "rgba(255,77,109,0.15)" : "rgba(96,165,250,0.15)",
                      color: e.event_type === "login" ? "#34d399" : e.event_type === "failed_login" ? "#ff4d6d" : "#60a5fa",
                    }}>{e.event_type}</span>
                  </td>
                  <td style={{ ...s.td, fontFamily: "monospace", fontSize: 10 }}>{e.user_id?.slice(0, 12)}</td>
                  <td style={{ ...s.td, fontFamily: "monospace", fontSize: 10 }}>{e.ip_address}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!filtered || filtered.length === 0) && (
            <div style={{ textAlign: "center", padding: 20, color: COLORS.muted, fontSize: 12 }}>No audit events</div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN ADMIN PANEL
// ═══════════════════════════════════════════════════════════

export default function AdminPanel() {
  const [tab, setTab] = useState("users");
  const user = getUser();
  const isSuperAdmin = user?.role === "super_admin";

  const tabs = [
    { id: "users", label: "Users" },
    ...(isSuperAdmin ? [{ id: "customers", label: "Customers" }] : []),
    { id: "audit", label: "Audit Log" },
    ...(isSuperAdmin ? [{ id: "ingestion", label: "Data Ingestion" }] : []),
    ...(isSuperAdmin ? [{ id: "linkedin", label: "LinkedIn Agent" }] : []),
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 20, overflowX: "auto" }}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "8px 18px", borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
            background: tab === t.id ? "rgba(0,229,255,0.1)" : "#131920",
            color: tab === t.id ? COLORS.primary : COLORS.muted,
            border: tab === t.id ? "1px solid rgba(0,229,255,0.3)" : `1px solid ${COLORS.border}`,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "users" && <UserManagement />}
      {tab === "customers" && isSuperAdmin && <CustomerManagement />}
      {tab === "audit" && <EnhancedAuditLog />}
      {tab === "ingestion" && isSuperAdmin && <IngestionPanel />}
      {tab === "linkedin" && isSuperAdmin && <LinkedInAgentPanel />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

function fmtDate(d) {
  if (!d) return "\u2014";
  try { return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
}

const s = {
  secTitle: { fontSize: 14, fontWeight: 700, color: COLORS.text },
  addBtn: { background: "linear-gradient(135deg, #00e5ff, #0099cc)", border: "none", borderRadius: 6, color: "#000", padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  form: { background: "#0d1117", border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 16, marginBottom: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 },
  input: { background: "#131920", border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "8px 12px", color: COLORS.text, fontSize: 12, fontFamily: "inherit", outline: "none" },
  submitBtn: { background: "#00e5ff", border: "none", borderRadius: 6, color: "#000", padding: "8px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  retryBtn: { background: "rgba(255,77,109,0.15)", border: "1px solid rgba(255,77,109,0.3)", borderRadius: 6, color: "#ff4d6d", padding: "4px 10px", fontSize: 10, cursor: "pointer", fontFamily: "inherit" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "8px 10px", fontSize: 9, fontWeight: 600, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase", borderBottom: `1px solid ${COLORS.border}`, position: "sticky", top: 0, background: COLORS.bg },
  td: { padding: "10px 10px", fontSize: 12, color: "#8b949e", borderBottom: "1px solid rgba(255,255,255,0.04)" },
  badge: { padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, display: "inline-block" },
  filterBtn: { borderRadius: 6, padding: "5px 12px", fontSize: 10, cursor: "pointer", fontWeight: 600, fontFamily: "inherit", border: `1px solid ${COLORS.border}` },
};
